import { SessionRepository } from "../../domain/repository/SessionRepository";
import { Either, Left, Right } from "../../core/types/Either";
import { SessionLocalDataSource } from "../dataSource/SessionLocalDataSource";

export class SessionRepositoryImpl extends SessionRepository {
  localDataSource: SessionLocalDataSource;

  constructor(localDataSource: SessionLocalDataSource) {
    super();
    this.localDataSource = localDataSource;
  }

  async touchActivity(): Promise<Either<Error, void>> {
    try {
      return Right(this.localDataSource.setLastActivity(Date.now()));
    } catch (e) {
      return Left(e);
    }
  }

  /**
   * Fails CLOSED. An absent or unparseable last-activity value reports a
   * fully-idle session rather than a fresh one, so a cleared or blocked
   * cookie ends the session instead of silently disabling the control.
   *
   * This cannot lock anyone out of a working browser: the session token is
   * itself a cookie written by the same helper, so a member whose cookies are
   * unavailable was never signed in. Failing closed therefore costs nothing
   * real, while failing open would mean anyone who cleared cookies
   * mid-session kept an idle session alive indefinitely — which is exactly
   * the HITRUST finding this control exists to close.
   */
  async getIdleMilliseconds(): Promise<Either<Error, number>> {
    try {
      const stored = this.localDataSource.getLastActivity();
      const lastActivity = Number(stored);
      if (!stored || !Number.isFinite(lastActivity)) {
        return Right(Number.MAX_SAFE_INTEGER);
      }
      return Right(Date.now() - lastActivity);
    } catch (e) {
      return Left(e);
    }
  }

  async clearActivity(): Promise<Either<Error, void>> {
    try {
      return Right(this.localDataSource.unsetLastActivity());
    } catch (e) {
      return Left(e);
    }
  }

  async setEndedReason(reason: string): Promise<Either<Error, void>> {
    try {
      return Right(this.localDataSource.setEndedReason(reason));
    } catch (e) {
      return Left(e);
    }
  }

  /**
   * Read-and-clear: the banner is shown once, so leaving the value in place
   * would re-show "you were signed out for inactivity" on every later visit
   * to /login.
   */
  async consumeEndedReason(): Promise<Either<Error, string>> {
    try {
      const reason = this.localDataSource.getEndedReason();
      if (reason) {
        this.localDataSource.unsetEndedReason();
      }
      return Right(reason);
    } catch (e) {
      return Left(e);
    }
  }
}
